import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Palette, ArrowLeft, User, Building, Frame, Check, TrendingUp, Heart, Leaf, Globe, Eye, Loader2 } from 'lucide-react';

// Google OAuth URL - Emergent managed
const EMERGENT_AUTH_BASE = 'https://demobackend.emergentagent.com/auth/v1/env/oauth';
const REDIRECT_URI = `${window.location.origin}/auth/callback`;

const LoginPage = () => {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [userTypes, setUserTypes] = useState(['artist']);  // 改为数组，支持多选
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 用户类型选项 - 使用翻译键
  const userTypeOptions = [
    { value: 'artist', labelKey: 'auth.userTypes.artist', descKey: 'auth.userTypes.artistDesc', icon: Palette, color: 'from-purple-500 to-pink-500' },
    { value: 'collector', labelKey: 'auth.userTypes.collector', descKey: 'auth.userTypes.collectorDesc', icon: User, color: 'from-amber-500 to-orange-500' },
    { value: 'museum', labelKey: 'auth.userTypes.museum', descKey: 'auth.userTypes.museumDesc', icon: Building, color: 'from-blue-500 to-cyan-500' },
    { value: 'curator', labelKey: 'auth.userTypes.curator', descKey: 'auth.userTypes.curatorDesc', icon: Frame, color: 'from-emerald-500 to-teal-500' },
    { value: 'investor', labelKey: 'auth.userTypes.investor', descKey: 'auth.userTypes.investorDesc', icon: TrendingUp, color: 'from-indigo-500 to-violet-500' },
    { value: 'art_lover', labelKey: 'auth.userTypes.artLover', descKey: 'auth.userTypes.artLoverDesc', icon: Heart, color: 'from-rose-500 to-pink-500' },
    { value: 'vegan', labelKey: 'auth.userTypes.vegan', descKey: 'auth.userTypes.veganDesc', icon: Leaf, color: 'from-green-500 to-emerald-500' },
    { value: 'eco_supporter', labelKey: 'auth.userTypes.ecoSupporter', descKey: 'auth.userTypes.ecoSupporterDesc', icon: Globe, color: 'from-cyan-500 to-blue-500' },
    { value: 'browser', labelKey: 'auth.userTypes.browser', descKey: 'auth.userTypes.browserDesc', icon: Eye, color: 'from-gray-500 to-slate-500' },
  ];

  // 切换用户类型选择
  const toggleUserType = (typeValue) => {
    if (userTypes.includes(typeValue)) {
      // 如果已选中，取消选择（但至少保留一个）
      if (userTypes.length > 1) {
        setUserTypes(userTypes.filter(t => t !== typeValue));
      }
    } else {
      // 添加选择
      setUserTypes([...userTypes, typeValue]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success(t('auth.loginSuccess'));
      } else {
        await register(name, email, password, phone, userTypes);
        toast.success(t('auth.registerSuccess'));
      }
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth login handler
  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    // Redirect to Emergent Google OAuth
    const googleAuthUrl = `${EMERGENT_AUTH_BASE}/google?redirect_uri=${encodeURIComponent(REDIRECT_URI)}`;
    window.location.href = googleAuthUrl;
  };

  return (
    <div className="min-h-screen bg-amber-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="login-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img loading="lazy"
              src="/logo.jpeg" 
              alt="ArtCanvas Logo" 
              className="w-16 h-16 rounded-full object-cover shadow-md"
              data-testid="login-logo-img"
            />
          </div>
          <CardTitle className="text-2xl">{t('siteName')}</CardTitle>
          <CardDescription>{mode === 'login' ? t('auth.login') : t('auth.register')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={mode} onValueChange={setMode} className="mb-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">{t('auth.login')}</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">{t('auth.register')}</TabsTrigger>
            </TabsList>
          </Tabs>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">{t('auth.name')}</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="name-input"
                  />
                </div>
                
                {/* 用户类型选择 - 多选 */}
                <div className="space-y-3">
                  <Label>{t('auth.iAm')} <span className="text-amber-500 text-xs">({t('auth.multiSelect')})</span></Label>
                  <div className="grid grid-cols-3 gap-2">
                    {userTypeOptions.map((type) => {
                      const isSelected = userTypes.includes(type.value);
                      const Icon = type.icon;
                      return (
                        <div
                          key={type.value}
                          className={`relative p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-amber-500 bg-amber-50' 
                              : 'border-amber-200 hover:border-amber-300'
                          }`}
                          onClick={() => toggleUserType(type.value)}
                          data-testid={`user-type-${type.value}`}
                        >
                          {isSelected && (
                            <div className="absolute top-2 right-2">
                              <Check className="w-4 h-4 text-amber-600" />
                            </div>
                          )}
                          <Icon className={`w-5 h-5 mb-1 ${isSelected ? 'text-amber-600' : 'text-amber-600'}`} />
                          <div className="font-medium text-sm">{t(type.labelKey)}</div>
                          <div className="text-xs text-amber-600 line-clamp-1">{t(type.descKey)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
              />
            </div>
            {mode === 'register' && (
              <div className="space-y-2">
                <Label htmlFor="phone">{t('checkout.phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('common.phonePlaceholder')}
                  data-testid="phone-input"
                />
              </div>
            )}
            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" disabled={loading} data-testid="submit-button">
              {loading ? t('checkout.processing') : mode === 'login' ? t('auth.login') : t('auth.register')}
            </Button>
            {mode === 'login' && (
              <Button 
                type="button" 
                variant="link" 
                className="w-full text-amber-600 hover:text-amber-600"
                onClick={() => navigate('/forgot-password')}
                data-testid="forgot-password-link"
              >
                {t('auth.forgotPassword', '忘记密码？')}
              </Button>
            )}
          </form>
          
          {/* Social Login Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-amber-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-amber-50 text-amber-600">
                {t('auth.orContinueWith', '或使用以下方式')}
              </span>
            </div>
          </div>

          {/* Google Login Button - Coming Soon */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 flex items-center justify-center gap-3 border-amber-200 bg-amber-50/50 text-amber-500 cursor-not-allowed"
            disabled={true}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 opacity-50" viewBox="0 0 24 24">
              <path
                fill="#9CA3AF"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#9CA3AF"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#9CA3AF"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#9CA3AF"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>Google {t('auth.comingSoon', '(即将推出)')}</span>
          </Button>
          
          {/* Future social login buttons placeholder */}
          <p className="text-xs text-center text-amber-500 mt-4">
            {t('auth.moreSocialLoginSoon', 'More login options coming soon')}
          </p>
        </CardContent>
        <CardFooter className="justify-center">
          <Button variant="link" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('common.backToHome')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default LoginPage;
